import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { CircleDot, Plus, Upload, Search, Edit3, Trash2, Package } from 'lucide-react'

const API = '' // ruta relativa — el proxy de Vite reenvía a Next y preserva cookies

interface TireListItem {
  id: string
  sku: string
  name: string
  price: number
  salePrice: number | null
  stock: number
  image: string
  isActive: boolean
  isOnSale: boolean
  spec: {
    size: string
    width: number
    aspectRatio: number
    rimDiameter: number
    loadIndex: number | null
    speedRating: string | null
    season: string
    runFlat: boolean
    brand: string
    brandId: string
    category: string
    categoryId: string
    pattern: string | null
    patternId: string | null
  } | null
}

interface Options {
  brands: { id: string; name: string }[]
  categories: { id: string; name: string }[]
  constants: { widths: number[]; rimDiameters: number[] }
}

export function Llantas() {
  const [items, setItems] = useState<TireListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [brandFilter, setBrandFilter] = useState('')
  const [catFilter, setCatFilter] = useState('')
  const [rimFilter, setRimFilter] = useState('')
  const [opts, setOpts] = useState<Options | null>(null)

  useEffect(() => {
    fetch(`${API}/api/admin/llantas/options`)
      .then((r) => r.json())
      .then(setOpts)
      .catch(() => null)
  }, [])

  async function load() {
    setLoading(true)
    const params = new URLSearchParams({ page: String(page), pageSize: '20' })
    if (q) params.set('q', q)
    if (brandFilter) params.set('tireBrandId', brandFilter)
    if (catFilter) params.set('tireCategoryId', catFilter)
    if (rimFilter) params.set('rim', rimFilter)
    const res = await fetch(`${API}/api/admin/llantas?${params}`)
    const data = await res.json()
    setItems(data.items || [])
    setTotal(data.total || 0)
    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  async function eliminar(id: string) {
    if (!confirm('¿Marcar esta llanta como inactiva?')) return
    await fetch(`${API}/api/admin/llantas/${id}`, { method: 'DELETE' })
    load()
  }

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-extrabold text-ink-900">
            <CircleDot className="size-6 text-brand-600" />
            Llantas
          </h1>
          <p className="text-sm text-ink-500">
            {total.toLocaleString('es-MX')} llantas en catálogo
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/llantas/carga-masiva"
            className="inline-flex items-center gap-2 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-semibold text-ink-700 hover:border-brand-300 hover:text-brand-700"
          >
            <Upload className="size-4" /> Carga masiva
          </Link>
          <Link
            to="/llantas/nueva"
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus className="size-4" /> Nueva llanta
          </Link>
        </div>
      </header>

      {/* Filtros */}
      <div className="rounded-2xl border border-ink-200 bg-white p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-400" />
            <input
              type="search"
              placeholder="Buscar por SKU o nombre…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value)
                setPage(1)
              }}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              className="h-10 w-full rounded-lg border border-ink-200 bg-white pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none"
            />
          </div>
          <select
            value={brandFilter}
            onChange={(e) => {
              setBrandFilter(e.target.value)
              setPage(1)
            }}
            className="h-10 w-full rounded-lg border border-ink-200 bg-white px-3 text-sm focus:border-brand-500 focus:outline-none"
          >
            <option value="">Todas las marcas</option>
            {opts?.brands.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <select
            value={catFilter}
            onChange={(e) => {
              setCatFilter(e.target.value)
              setPage(1)
            }}
            className="h-10 w-full rounded-lg border border-ink-200 bg-white px-3 text-sm focus:border-brand-500 focus:outline-none"
          >
            <option value="">Todas las secciones</option>
            {opts?.categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={rimFilter}
            onChange={(e) => {
              setRimFilter(e.target.value)
              setPage(1)
            }}
            className="h-10 w-full rounded-lg border border-ink-200 bg-white px-3 text-sm focus:border-brand-500 focus:outline-none"
          >
            <option value="">Todos los rines</option>
            {opts?.constants.rimDiameters.map((r) => (
              <option key={r} value={r}>R{r}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={load}
            className="h-10 rounded-lg bg-ink-900 px-3 text-sm font-semibold text-white hover:bg-brand-600"
          >
            Aplicar filtros
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-ink-50">
            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500">
              <th className="px-4 py-3">SKU</th>
              <th className="px-4 py-3">Llanta</th>
              <th className="px-4 py-3">Medida</th>
              <th className="px-4 py-3">Sección</th>
              <th className="px-4 py-3 text-right">Precio</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-ink-400">
                  Cargando…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-ink-400">
                  <Package className="mx-auto mb-2 size-10" />
                  <p>Sin llantas. Comienza creando una o subiendo el CSV.</p>
                </td>
              </tr>
            ) : (
              items.map((t) => (
                <tr key={t.id} className="hover:bg-ink-50/50">
                  <td className="px-4 py-3 font-mono text-xs">{t.sku}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-ink-900">{t.name}</div>
                    <div className="text-xs text-ink-500">
                      {t.spec?.brand}
                      {t.spec?.pattern && ` · ${t.spec.pattern}`}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {t.spec?.size}
                    {t.spec?.loadIndex && t.spec?.speedRating
                      ? ` ${t.spec.loadIndex}${t.spec.speedRating}`
                      : ''}
                    {t.spec?.runFlat && (
                      <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">RF</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{t.spec?.category}</td>
                  <td className="px-4 py-3 text-right">
                    {t.salePrice ? (
                      <div>
                        <div className="font-semibold text-emerald-700">
                          ${t.salePrice.toLocaleString('es-MX')}
                        </div>
                        <div className="text-xs text-ink-400 line-through">
                          ${t.price.toLocaleString('es-MX')}
                        </div>
                      </div>
                    ) : (
                      <div className="font-semibold">${t.price.toLocaleString('es-MX')}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className={
                        t.stock === 0
                          ? 'text-red-600'
                          : t.stock < 5
                            ? 'text-amber-600'
                            : 'text-ink-700'
                      }
                    >
                      {t.stock}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {t.isActive ? (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                        Activa
                      </span>
                    ) : (
                      <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-500">
                        Inactiva
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to="/llantas/$id"
                      params={{ id: t.id }}
                      className="mr-1 inline-flex items-center gap-1 rounded p-1.5 text-ink-500 hover:bg-brand-50 hover:text-brand-700"
                    >
                      <Edit3 className="size-4" />
                    </Link>
                    <button
                      type="button"
                      onClick={() => eliminar(t.id)}
                      className="inline-flex items-center gap-1 rounded p-1.5 text-ink-500 hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {total > 20 && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-ink-500">
            Página {page} de {Math.ceil(total / 20)}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 font-semibold disabled:opacity-40"
            >
              Anterior
            </button>
            <button
              type="button"
              disabled={page >= Math.ceil(total / 20)}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 font-semibold disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
